/**
 * Campaigns, invites and the shared city session (campaigns SRD Phase 1). Plain prose, no em dashes (SRD NF-6).
 * Keys live under `camp.` so they never collide with the older `play.campaign.` strings.
 */
export const campaignsEn = {
  // Create
  'camp.create.title': 'Start a campaign',
  'camp.create.intro': 'Name your game, invite up to five friends with one link, and play together in {city}.',
  'camp.create.name': 'Campaign name',
  'camp.create.namePlaceholder': 'Lanterns over the city',
  'camp.create.pitch': 'What is it about? (optional)',
  'camp.create.pitchPlaceholder': 'One or two lines your friends will see on the invite.',
  'camp.create.rules': 'Rules',
  'camp.create.world': 'Where you play',
  'camp.create.seats': 'Seats',
  'camp.create.seatsValue': 'You as the GM, plus up to five players',
  'camp.create.who': 'Who can join',
  'camp.create.whoValue': 'Anyone with your invite link. Share it only with people you want at your table.',
  'camp.create.submit': 'Create campaign',
  'camp.create.creating': 'Creating...',
  'camp.create.failed': 'The campaign could not be created. Please try again.',
  'camp.create.nameRequired': 'Give your campaign a name.',

  // List
  'camp.list.heading': 'My campaigns',
  'camp.list.empty': 'You are not in any campaigns yet. Start one, or ask a friend for their invite link.',
  'camp.list.error': 'Your campaigns could not be loaded.',
  'camp.list.retry': 'Try again',
  'camp.list.youGm': 'You are the GM',
  'camp.list.youPlay': 'You are a player',
  'camp.list.players': '{count} of {max} players',
  'camp.list.open': 'Open campaign',
  'camp.list.signIn': 'Sign in to see your campaigns.',

  // Campaign page
  'camp.page.back': 'Back to Play',
  'camp.page.loading': 'Opening the campaign...',
  'camp.page.notFound': 'This campaign is private, or it does not exist. If a friend invited you, open their invite link.',
  'camp.page.error': 'The campaign could not be loaded.',
  'camp.page.signIn': 'Sign in to open this campaign.',
  'camp.page.rulesIn': 'Rules: {system} · Place: {city}',
  'camp.page.seatsHeading': 'The table',
  'camp.page.gmSeat': 'GM',
  'camp.page.playerSeat': 'Player',
  'camp.page.emptySeat': 'Open seat',
  'camp.page.you': 'You',
  'camp.page.noCharacter': 'No character yet',
  'camp.page.remove': 'Remove',
  'camp.page.removeConfirm': 'Remove {name} from the campaign? They will be taken out of the city at once and will need a new invite to come back.',
  'camp.page.removeFailed': 'That player could not be removed. Please try again.',
  'camp.page.aPlayer': 'this player',

  // Invite panel (GM)
  'camp.invite.heading': 'Invite friends',
  'camp.invite.intro': 'Send this link to the people you want at your table. Each person can use it once. It works for 7 days, or until all its seats are taken.',
  'camp.invite.make': 'Make an invite link',
  'camp.invite.remake': 'Make a new link',
  'camp.invite.remakeNote': 'A new link stops the old one from working.',
  'camp.invite.copy': 'Copy link',
  'camp.invite.copied': 'Copied',
  'camp.invite.expires': 'Works until {date}. Used {uses} of {max}.',
  'camp.invite.activeNoLink': 'You have a working link (used {uses} of {max}, works until {date}). We show a link only once, when you make it, so make a new one if you need to send it again.',
  'camp.invite.full': 'Every seat is taken.',
  'camp.invite.failed': 'The link could not be made. Please try again.',

  // Character
  'camp.char.heading': 'Your character',
  'camp.char.bring': 'Play as {name}',
  'camp.char.make': 'Make a character',
  'camp.char.makeNew': 'Make a new character',
  'camp.char.playing': 'You are playing {name}.',
  'camp.char.none': 'Pick the character you will play in this campaign.',
  'camp.char.failed': 'Your character could not be saved. Please try again.',

  // Session
  'camp.session.heading': 'Session',
  'camp.session.open': 'The session is open. Everyone at the table with a character can enter {city}.',
  'camp.session.closed': 'The session is closed. You can enter when your GM starts it, so check back here then.',
  'camp.session.closedGm': 'The session is closed. Players cannot enter until you start it.',
  'camp.session.start': 'Start session',
  'camp.session.end': 'End session',
  'camp.session.endConfirm': 'End the session? Players leave the city, the clock stops and the game is saved. The next session picks up where this one stopped.',
  'camp.session.enter': 'Enter {city}',
  'camp.session.enterGm': 'Enter {city} as GM',
  'camp.session.online': '{count} in the city now',
  'camp.session.failed': 'The session could not be changed. Please try again.',
  'camp.session.unknown': 'The city could not say whether the session is open.',
  'camp.session.needCharacter': 'Pick your character first.',

  // Leave
  'camp.leave.button': 'Leave campaign',
  'camp.leave.confirm': 'Leave this campaign? You will need a new invite to come back.',
  'camp.leave.failed': 'You could not leave the campaign. Please try again.',

  // Invite page
  'camp.join.seoTitle': 'Campaign invite',
  'camp.join.loading': 'Opening the invite...',
  'camp.join.invited': '{host} invited you to play in {title}.',
  'camp.join.invitedNoHost': 'You are invited to play in {title}.',
  'camp.join.rulesIn': 'Rules: {system} · Place: {city}',
  'camp.join.seatsLeft': '{count} seats left',
  'camp.join.seatsLeft.one': '1 seat left',
  'camp.join.accept': 'Accept and take a seat',
  'camp.join.decline': 'No thanks',
  'camp.join.declined': 'No problem. Your GM is not told. You can close this page.',
  'camp.join.signIn': 'Sign in to accept',
  'camp.join.register': 'Create an account',
  'camp.join.signInNote': 'Play online in your browser. You need a free account to take a seat (13 or older), and you will come straight back here.',
  'camp.join.already': 'You are already at this table.',
  'camp.join.openCampaign': 'Open the campaign',
  'camp.join.expired': 'This invite has expired. Ask your GM for a new link.',
  'camp.join.revoked': 'This invite no longer works. Ask your GM for a new link.',
  'camp.join.used': 'This invite has been used. Ask your GM for a new link.',
  'camp.join.full': 'This table is full. If a seat opens up, ask your GM for a new link.',
  'camp.join.notFound': 'This invite link does not work. Check that you copied all of it, or ask your GM for a new one.',
  'camp.join.failed': 'Something went wrong. Please try again.',

  // City: the shared table panel
  'city.table.title': 'Your table',
  'city.table.gmIntro': 'You are the GM. The clock, rests, travel and saves are yours; your players walk the city with you.',
  'city.table.playerIntro': 'Your GM runs the clock, rests, travel and saves. Everyone at your table is shown in gold.',
  'city.table.pause': 'Pause the clock',
  'city.table.resume': 'Start the clock',
  'city.table.skip': 'Skip {n} minutes',
  'city.table.gather': 'Gather the party here',
  'city.table.end': 'End session',
  'city.table.endConfirm': 'End the session? Players leave the city, the clock stops and the game is saved for next time.',
  'city.table.back': 'Campaign page',
  'city.table.paused': 'The clock is paused.',
  'city.sessionClosed': 'The session has not started yet, or it has ended. Your GM opens it from the campaign page.',
  'city.notMember': 'You are not at this table. If a friend invited you, open their invite link first.',
  'city.removed': 'You are no longer at this table. To come back, ask the GM for a new invite.',
  'city.campaignsDown': 'We cannot check your seat right now. Please try again in a minute.',
}
